<!-- Toast -->
<div *ngIf="notification.show" class="fixed top-6 right-6 z-50 max-w-md">
  <div class="flex items-start gap-3 rounded-xl px-4 py-3 shadow-lg text-white"
    [ngClass]="{
      'bg-emerald-600': notification.type === 'success',
      'bg-red-600': notification.type === 'error',
      'bg-blue-600': notification.type === 'info'
    }">
    <i class="fas mt-0.5" [ngClass]="{
      'fa-check-circle': notification.type === 'success',
      'fa-exclamation-circle': notification.type === 'error',
      'fa-info-circle': notification.type === 'info'
    }"></i>
    <span class="text-sm font-medium flex-1">{{ notification.message }}</span>
    <button type="button" (click)="dismissNotification()" class="text-white/80 hover:text-white" aria-label="Dismiss">
      <i class="fas fa-times"></i>
    </button>
  </div>
</div>

<div class="flex h-screen overflow-hidden bg-gray-50">
  <!-- Sidebar - Zone List -->
  <div class="w-80 bg-white shadow-lg border-r border-gray-200 flex flex-col overflow-hidden">
    <!-- Header -->
    <div class="p-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-green-500">
      <h2 class="text-xl font-bold text-white flex items-center">
        <i class="fas fa-map-marked-alt mr-2"></i>
        Geo Zones Manager
      </h2>
      <p class="text-sm text-green-50 mt-1">Manage service zones</p>
    </div>

    <!-- Stats -->
    <div class="p-4 bg-gradient-to-br from-blue-50 to-green-50 border-b border-gray-200">
      <div class="grid grid-cols-2 gap-2">
        <div class="bg-white rounded-lg p-2 shadow-sm">
          <div class="text-xs text-gray-500">Total Zones</div>
          <div class="text-lg font-bold text-green-600">{{ getZoneStats().total }}</div>
        </div>
        <div class="bg-white rounded-lg p-2 shadow-sm">
          <div class="text-xs text-gray-500">Visible</div>
          <div class="text-lg font-bold text-blue-600">{{ getZoneStats().visible }}</div>
        </div>
      </div>
    </div>

    <!-- Search and Controls -->
    <div class="p-4 border-b border-gray-200 space-y-3">
      <!-- Search -->
      <div class="relative">
        <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
        <input
          type="text"
          [(ngModel)]="searchTerm"
          (ngModelChange)="onSearchChange()"
          placeholder="Search zones..."
          class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
      </div>

      <!-- Visibility Controls -->
      <div class="flex gap-2">
        <button
          (click)="showAllZones()"
          class="flex-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
        >
          <i class="fas fa-eye mr-1"></i>Show All
        </button>
        <button
          (click)="hideAllZones()"
          class="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <i class="fas fa-eye-slash mr-1"></i>Hide All
        </button>
      </div>

      <!-- Export Dropdown -->
      <div class="relative">
        <button
          class="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 transition-all shadow-sm font-medium flex items-center justify-center"
          onclick="this.nextElementSibling.classList.toggle('hidden')"
        >
          <i class="fas fa-download mr-2"></i>
          Export Zones
          <i class="fas fa-chevron-down ml-2 text-xs"></i>
        </button>
        <div class="hidden absolute z-10 w-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 py-1">
          <button
            (click)="exportZones('json')"
            class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 flex items-center"
          >
            <i class="fas fa-file-code mr-2 w-4"></i>JSON Format
          </button>
          <button
            (click)="exportZones('geojson')"
            class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 flex items-center"
          >
            <i class="fas fa-globe mr-2 w-4"></i>GeoJSON Format
          </button>
          <button
            (click)="exportZones('kml')"
            class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 flex items-center"
          >
            <i class="fas fa-map mr-2 w-4"></i>KML Format
          </button>
          <button
            (click)="exportZones('csv')"
            class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 flex items-center"
          >
            <i class="fas fa-table mr-2 w-4"></i>CSV Format
          </button>
        </div>
      </div>

      <!-- Check Overlaps Button -->
      <button
        (click)="checkOverlaps()"
        class="w-full px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-500 text-white rounded-lg hover:from-orange-700 hover:to-orange-600 transition-all shadow-sm font-medium flex items-center justify-center"
        title="Check if any zones overlap"
      >
        <i class="fas fa-project-diagram mr-2"></i>
        Check Overlaps
      </button>

      <!-- New Zone -->
      <button
        *appHasPermission="[P.GEOZONE_MANAGE, P.IS_CC_ADMIN]"
        (click)="startCreate()"
        [disabled]="isCreating || saving"
        class="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg hover:from-green-700 hover:to-green-600 transition-all shadow-sm font-medium flex items-center justify-center disabled:opacity-50"
      >
        <i class="fas fa-plus mr-2"></i>
        New Zone
      </button>
    </div>

    <!-- Zone List -->
    <div class="flex-1 overflow-y-auto">
      <div *ngIf="loading" class="p-8 text-center">
        <i class="fas fa-spinner fa-spin text-3xl text-green-600 mb-2"></i>
        <p class="text-gray-500">Loading zones...</p>
      </div>

      <div *ngIf="!loading && filteredZones.length === 0" class="p-8 text-center">
        <i class="fas fa-search text-3xl text-gray-300 mb-2"></i>
        <p class="text-gray-500">No zones found</p>
      </div>

      <div *ngIf="!loading" class="p-2 space-y-2">
        <div
          *ngFor="let zone of filteredZones; let i = index"
          class="group bg-white rounded-lg border hover:border-green-400 hover:shadow-md transition-all"
          [class.border-gray-200]="selectedZone?.id !== zone.id"
          [class.border-green-500]="selectedZone?.id === zone.id"
          [class.border-2]="selectedZone?.id === zone.id"
          [class.shadow-md]="selectedZone?.id === zone.id"
        >
          <div class="p-3">
            <div class="flex items-start justify-between">
              <div class="flex-1 min-w-0 cursor-pointer" (click)="selectZone(zone, $event)">
                <div class="flex items-center gap-2 mb-1">
                  <div
                    class="w-3 h-3 rounded-full"
                    [style.background-color]="zone.visible ? getZoneColor(zone, zones.indexOf(zone)) : '#cbd5e0'"
                  ></div>
                  <h3 class="font-semibold text-sm text-gray-800 truncate">{{ zone.name }}</h3>
                </div>
                <p class="text-xs text-gray-500 truncate mb-1">{{ zone.id }}</p>
                <div class="flex items-center gap-2 text-xs text-gray-400">
                  <span>
                    <i class="fas fa-draw-polygon mr-1"></i>
                    {{ zone.coordinate?.coordinates?.length || 0 }} points
                  </span>
                  <span *ngIf="zone.disabled" class="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-semibold">
                    <i class="fas fa-ban mr-1"></i>Disabled
                  </span>
                </div>
              </div>
              
              <!-- Visibility Toggle -->
              <button
                (click)="toggleZoneVisibility(zone); $event.stopPropagation()"
                class="ml-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                [class.text-green-600]="zone.visible"
                [class.text-gray-400]="!zone.visible"
              >
                <i class="fas" [class.fa-eye]="zone.visible" [class.fa-eye-slash]="!zone.visible"></i>
              </button>
            </div>

            <!-- Zone Actions -->
            <div class="mt-2 pt-2 border-t border-gray-100 flex gap-1">
              <button
                (click)="zoomToZone(zone, $event)"
                class="flex-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
              >
                <i class="fas fa-search-location mr-1"></i>Zoom
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Map Container -->
  <div class="flex-1 relative">
    <!-- Map Type Controls -->
    <div class="absolute top-4 left-4 z-10 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      <button
        (click)="changeMapType('roadmap')"
        class="block w-full px-4 py-2 text-sm text-left transition-colors border-b border-gray-100"
        [class.bg-green-50]="mapTypeId === 'roadmap'"
        [class.text-green-600]="mapTypeId === 'roadmap'"
        [class.font-semibold]="mapTypeId === 'roadmap'"
        [class.text-gray-700]="mapTypeId !== 'roadmap'"
        [class.hover:bg-gray-50]="mapTypeId !== 'roadmap'"
      >
        <i class="fas fa-map mr-2 w-4"></i>
        Roadmap
      </button>
      <button
        (click)="changeMapType('satellite')"
        class="block w-full px-4 py-2 text-sm text-left transition-colors border-b border-gray-100"
        [class.bg-green-50]="mapTypeId === 'satellite'"
        [class.text-green-600]="mapTypeId === 'satellite'"
        [class.font-semibold]="mapTypeId === 'satellite'"
        [class.text-gray-700]="mapTypeId !== 'satellite'"
        [class.hover:bg-gray-50]="mapTypeId !== 'satellite'"
      >
        <i class="fas fa-satellite mr-2 w-4"></i>
        Satellite
      </button>
      <button
        (click)="changeMapType('hybrid')"
        class="block w-full px-4 py-2 text-sm text-left transition-colors border-b border-gray-100"
        [class.bg-green-50]="mapTypeId === 'hybrid'"
        [class.text-green-600]="mapTypeId === 'hybrid'"
        [class.font-semibold]="mapTypeId === 'hybrid'"
        [class.text-gray-700]="mapTypeId !== 'hybrid'"
        [class.hover:bg-gray-50]="mapTypeId !== 'hybrid'"
      >
        <i class="fas fa-layer-group mr-2 w-4"></i>
        Hybrid
      </button>
      <button
        (click)="changeMapType('terrain')"
        class="block w-full px-4 py-2 text-sm text-left transition-colors"
        [class.bg-green-50]="mapTypeId === 'terrain'"
        [class.text-green-600]="mapTypeId === 'terrain'"
        [class.font-semibold]="mapTypeId === 'terrain'"
        [class.text-gray-700]="mapTypeId !== 'terrain'"
        [class.hover:bg-gray-50]="mapTypeId !== 'terrain'"
      >
        <i class="fas fa-mountain mr-2 w-4"></i>
        Terrain
      </button>
    </div>

    <!-- Traffic Toggle -->
    <div class="absolute top-4 left-44 z-10">
      <button
        (click)="toggleTraffic()"
        class="px-4 py-2 bg-white rounded-lg shadow-lg border border-gray-200 text-sm font-medium transition-all"
        [class.bg-green-50]="showTraffic"
        [class.text-green-600]="showTraffic"
        [class.border-green-300]="showTraffic"
        [class.text-gray-700]="!showTraffic"
        [class.hover:bg-gray-50]="!showTraffic"
      >
        <i class="fas fa-traffic-light mr-2"></i>
        Traffic {{ showTraffic ? 'On' : 'Off' }}
      </button>
    </div>

    <!-- Selected Zone Info & Editor -->
    <div *ngIf="selectedZone || isCreating" class="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-[calc(100%-2rem)] sm:w-96 max-h-[calc(100vh-2rem)] overflow-y-auto">
      <div class="flex items-start justify-between mb-4">
        <div *ngIf="isCreating">
          <h3 class="font-bold text-gray-900 text-lg">New zone</h3>
          <p class="text-xs text-gray-500">Click on the map to draw the boundary, then fill in the details.</p>
        </div>
        <div *ngIf="!isCreating && selectedZone">
          <h3 class="font-bold text-gray-900 text-lg flex items-center gap-2">
            {{ selectedZone.name }}
            <span *ngIf="selectedZone.disabled" class="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-600">Disabled</span>
            <span *ngIf="!selectedZone.disabled" class="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">Active</span>
          </h3>
          <p class="text-xs text-gray-500 font-mono">{{ selectedZone.id }}</p>
        </div>
        <button
          (click)="cancelEditing(); selectedZone = null"
          [disabled]="saving"
          class="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div *ngIf="!editorOpen && selectedZone" class="space-y-4">
        <!-- Display Info -->
        <div class="grid grid-cols-2 gap-3 pb-4 border-b border-gray-100">
          <div class="bg-blue-50 p-2 rounded-lg">
            <p class="text-[10px] text-blue-600 uppercase font-semibold">Per Day Limit</p>
            <p class="text-lg font-bold text-blue-800">{{ selectedZone.maxAppointmentsPerDay || 'No limit' }}</p>
          </div>
          <div class="bg-indigo-50 p-2 rounded-lg">
            <p class="text-[10px] text-indigo-600 uppercase font-semibold">Per Slot Limit</p>
            <p class="text-lg font-bold text-indigo-800">{{ selectedZone.maxAppointmentsPerSlot || 'No limit' }}</p>
          </div>
        </div>

        <div class="space-y-2 text-sm text-gray-600">
          <div class="flex justify-between gap-3">
            <span class="text-gray-400 shrink-0">Service days:</span>
            <span class="font-semibold text-green-700 text-right">{{ getScheduleSummary(selectedZone) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-400">Pricing Group:</span>
            <span class="font-semibold text-green-700">{{ getPricingGroupName(selectedZone.pricingGroupId) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-400">District:</span>
            <span class="font-semibold text-green-700">{{ getDistrictName(selectedZone.districtId) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-400">Coordinates:</span>
            <span class="font-medium">{{ selectedZone.coordinate?.coordinates?.length || 0 }} points</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-400">Created:</span>
            <span class="font-medium">{{ selectedZone.createdAt | date:'short' }}</span>
          </div>
          <div *ngIf="selectedZone.updatedAt" class="flex justify-between">
            <span class="text-gray-400">Updated:</span>
            <span class="font-medium">{{ selectedZone.updatedAt | date:'short' }}</span>
          </div>
          <div *ngIf="selectedZone.disabled" class="flex justify-between text-gray-600 font-semibold">
            <span>Disabled since:</span>
            <span>{{ selectedZone.disabled | date:'short' }}</span>
          </div>
        </div>

        <app-booking-conflict-panel *ngIf="statusConflict" [conflict]="statusConflict"
          (dismiss)="statusConflict = null"></app-booking-conflict-panel>

        <div *appHasPermission="[P.GEOZONE_MANAGE, P.IS_CC_ADMIN]" class="flex gap-2 mt-4">
          <button
            (click)="startEditing()"
            class="flex-1 flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm font-medium"
          >
            <i class="fas fa-edit mr-2"></i>
            Edit
          </button>
          <button
            (click)="toggleZoneStatus()"
            [disabled]="togglingStatus"
            class="flex-1 flex items-center justify-center px-4 py-2 rounded-lg transition-colors shadow-sm font-medium border disabled:opacity-50"
            [ngClass]="selectedZone.disabled ? 'border-green-600 text-green-700 hover:bg-green-50' : 'border-red-300 text-red-600 hover:bg-red-50'"
          >
            <i class="fas mr-2" [ngClass]="togglingStatus ? 'fa-spinner fa-spin' : (selectedZone.disabled ? 'fa-toggle-on' : 'fa-ban')"></i>
            {{ selectedZone.disabled ? 'Enable' : 'Disable' }}
          </button>
        </div>
      </div>

      <!-- Create / Edit Form -->
      <app-zone-editor
        *ngIf="editorOpen"
        [zone]="isCreating ? null : selectedZone"
        [pricingGroups]="pricingGroups"
        [districts]="districts"
        [timeSlots]="timeSlots"
        [pointCount]="pointCount"
        [saving]="saving"
        [conflict]="editConflict"
        (save)="onEditorSave($event)"
        (cancel)="cancelEditing()"
        (conflictDismissed)="editConflict = null"
        (undoPoint)="undoLastPoint()"
        (clearPoints)="clearDraft()"
      ></app-zone-editor>
    </div>

    <!-- Loading Overlay -->
    <div *ngIf="loading" class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-20">
      <div class="text-center">
        <i class="fas fa-spinner fa-spin text-5xl text-green-600 mb-4"></i>
        <p class="text-lg font-medium text-gray-700">Loading map...</p>
      </div>
    </div>

    <!-- Map -->
    <div #mapContainer class="w-full h-full"></div>
  </div>
</div>

